import React from 'react';
import moment from 'moment';
import { parseGraphqlErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroupWithSelect from '@reverbdotcom/commons/src/components/form_group_with_select';
import FormGroupWithCheckbox from '@reverbdotcom/commons/src/components/form_group_with_checkbox';
import AlertBox from '@reverbdotcom/commons/src/components/alert_box';
import { MutationFn } from '../../lib/apollo_client';
import { V3 } from '../api_request';
import { Paths } from '../shared/url_helpers';
import {
  CoreUpdateUniversalPromoCampaign,
  CoreUniversalPromoCampaign,
  core_apimessages_Error,
  reverb_config_cache_ExchangeRate,
} from '../../schemas/rql';
import {
  getDisplayCategories,
  getDisplayCurrencyValues,
  getCurrenciesForSubmit,
  getCategoriesForSubmit,
  convertCurrencyValues,
  DisplayCurrency,
} from './form_helpers';
import CurrenciesInputs from './currencies_inputs';
import Categories from './categories';

interface IProps {
  campaign: CoreUniversalPromoCampaign.Universalpromocampaign;
  updateUniversalPromoCampaign: MutationFn<CoreUpdateUniversalPromoCampaign.Mutation, CoreUpdateUniversalPromoCampaign.Variables>;
  exchangeRates: reverb_config_cache_ExchangeRate[];
}

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';
const MARKETING_CHANNELS = [
  'direct_mail',
  'email',
  'event',
  'facebook',
  'influencer',
  'partner',
  'podcast',
  'print',
];

function marketingChannelsSelectFields() {
  return MARKETING_CHANNELS.map(channel => ({
    text: channel,
    value: channel,
  }));
}

export function UniversalPromoCampaignForm({ campaign, updateUniversalPromoCampaign, exchangeRates }: IProps) {
  const [name, setName] = React.useState(campaign?.name || '');
  const [code, setCode] = React.useState(campaign?.code || '');
  const [currencyValues, setCurrencyValues] = React.useState(getDisplayCurrencyValues(campaign?.universalPromoCurrencies));
  const [marketingChannel, setMarketingChannel] = React.useState(campaign?.marketingChannel || '');
  const [expiresAt, setExpiresAt] = React.useState(campaign?.expiresAt ? moment.utc(campaign.expiresAt).format(DATE_FORMAT) : '');
  const [redemptionLimit, setRedemptionLimit] = React.useState(campaign?.hasRedemptionLimit ? campaign.redemptionLimit.toString() : '');
  const [newBuyer, setNewBuyer] = React.useState(campaign?.newBuyer || false);
  const [categories, setCategories] = React.useState([]);
  const [errors, setErrors] = React.useState<core_apimessages_Error[]>([]);
  const [wasSuccess, setWasSuccess] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    V3.get(
      Paths.apiCategoriesFlat.expand({}),
    ).then((res) => {
      const displayCategories = getDisplayCategories(res.categories, campaign);
      setCategories(displayCategories);
    });
  }, []);

  const convertAndSetCurrencyValues = (primaryCurrencyValues: DisplayCurrency) => {
    const convertedCurrencyValues = convertCurrencyValues(currencyValues, exchangeRates, primaryCurrencyValues);

    setCurrencyValues(convertedCurrencyValues);
  };

  const handleSubmit = async (submit) => {
    submit.preventDefault();
    const categoriesForSubmit = getCategoriesForSubmit(categories);

    if (newBuyer && !categoriesForSubmit.length) {
      setErrors([{ message: 'At least one category needs to be selected when New Buyer Only is selected.' }]);
      window.scrollTo(0, 0);
      return;
    }

    setErrors([]);
    setWasSuccess(false);
    setIsSaving(true);

    const variables = {
      input: {
        universalPromoCampaign: {
          id: campaign ? campaign.id : null,
          name,
          code,
          marketingChannel,
          expiresAt: (expiresAt && moment.utc(expiresAt).format()) || null,
          newBuyer,
          universalPromoCurrencies: getCurrenciesForSubmit(currencyValues),
          categories: categoriesForSubmit,
          hasRedemptionLimit: !!redemptionLimit,
          redemptionLimit: redemptionLimit ? parseInt(redemptionLimit, 10) : null,
        },
      },
    };

    try {
      await updateUniversalPromoCampaign({ variables });
      setWasSuccess(true);
    } catch (e) {
      setErrors(parseGraphqlErrors(e));
    } finally {
      setIsSaving(false);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="width-75 mtb-3">
      <h2 className="mb-2">{`${campaign ? 'Edit' : 'New'} Promotional Campaign`}</h2>
      <div className="mb-2">
        {!!errors.length && (
          <AlertBox type="error">
            {errors.map((error) => {
              return (
                <li key={error.field + error.message}>
                  {error.message}
                </li>
              );
            })}
          </AlertBox>
        )}
        {wasSuccess && (
          <AlertBox type="success">
            Saved!
          </AlertBox>
        )}
      </div>
      <form onSubmit={handleSubmit}>
        <FormGroupWithInput
          inputName="name"
          label="Campaign Name"
          value={name}
          required
          tagRequired
          updateField={(fieldUpdate) => setName(fieldUpdate.name)}
        />
        <FormGroupWithInput
          inputName="code"
          label="Promo Code"
          value={code}
          required
          tagRequired
          disabled={!!campaign}
          updateField={(fieldUpdate) => setCode(fieldUpdate.code)}
        />
        <CurrenciesInputs
          currencyValues={currencyValues}
          setCurrencyValues={setCurrencyValues}
          onConvert={convertAndSetCurrencyValues}
          isEdit={!!campaign}
        />
        <FormGroupWithSelect
          inputName="marketingChannel"
          label="Marketing Channel"
          options={marketingChannelsSelectFields()}
          value={marketingChannel}
          updateField={(fieldUpdate) => setMarketingChannel(fieldUpdate.marketingChannel)}
          addEmptyOption
          required
          tagRequired
        />
        <FormGroupWithInput
          inputName="expiresAt"
          label="Expires At"
          value={expiresAt}
          helpText="Time is UTC"
          tagOptional
          inputType="datetime-local"
          updateField={(fieldUpdate) => setExpiresAt(fieldUpdate.expiresAt)}
        />
        <FormGroupWithInput
          inputName="redemptionLimit"
          label="Redemption Limit"
          value={redemptionLimit}
          tagOptional
          updateField={(fieldUpdate) => setRedemptionLimit(fieldUpdate.redemptionLimit)}
        />
        <FormGroupWithCheckbox
          inputName="newBuyer"
          label="New Buyer Only"
          checked={newBuyer}
          updateField={(fieldUpdate) => setNewBuyer(fieldUpdate.newBuyer)}
          helpText="At least one category needs to be selected when New Buyer Only is checked"
        />
        <Categories
          categories={categories}
          setCategories={setCategories}
        />
        <button
          className="button button--blue"
          type="submit"
          value="Submit"
          disabled={isSaving}
        >
          Submit
        </button>
      </form>
    </div>
  );
}

export default UniversalPromoCampaignForm;
